import React, { useCallback, useEffect, useRef, useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import styled from 'styled-components';
import { RequiredMark, TextField } from '../styled';
import { Country, State } from 'country-state-city';

const allCountries = Country.getAllCountries();
const US_FIRST = allCountries.filter(c => c.isoCode === 'US');
const REST = allCountries
  .filter(c => c.isoCode !== 'US')
  .sort((a, b) => a.name.localeCompare(b.name));
const COUNTRIES = [...US_FIRST, ...REST];

const HiddenMap = styled.div`
  display: none;
  width: 0;
  height: 0;
`;

const AutocompleteWrapper = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #cbd6e2;
  border-radius: 4px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  .loading {
    padding: 8px 16px;
    color: #666;
  }

  .suggestion {
    padding: 8px 16px;
    cursor: pointer;
    background-color: #fff;
    border-bottom: 1px solid #f0f0f0;

    &.active {
      background-color: #eaf0f6;
    }
  }
`;

const Placeholder = styled.span`
  color: #9e9e9e;
`;

function SuggestionsDropdown({ suggestions, loading, getSuggestionItemProps }) {
  if (!loading && suggestions.length === 0) return null;

  return (
    <Dropdown>
      {loading && <div className="loading">Loading...</div>}
      {suggestions.map(suggestion => (
        <div
          {...getSuggestionItemProps(suggestion, {
            className: suggestion.active ? 'suggestion active' : 'suggestion',
          })}
          key={suggestion.placeId}
        >
          {suggestion.description}
        </div>
      ))}
    </Dropdown>
  );
}

export default function SignupAddressSection({
  values,
  onChange,
  submitted = false,
  errors = {},
}) {
  const [streetInput, setStreetInput] = useState(values.streetAddress || '');
  const showError = field => submitted && !!errors[field];
  const errorText = field => (submitted ? errors[field] || '' : '');
  const mapRef = useRef(null);
  const [places, setPlaces] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [zipWarning, setZipWarning] = useState(false);

  useEffect(() => {
    if (mapRef.current && window.google) {
      setPlaces(new window.google.maps.places.PlacesService(mapRef.current));
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
    }
  }, []);

  const extractComponent = (components, type, useShort = false) => {
    const found = components.find(c => c.types.includes(type));
    return found ? (useShort ? found.short_name : found.long_name) : '';
  };

  const handleSelect = useCallback(
    (address, placeId) => {
      setStreetInput(address);
      const token = sessionToken;
      setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
      if (!places || !placeId) {
        onChange({ ...values, streetAddress: address });
        return;
      }
      places.getDetails(
        { placeId, fields: ['address_components'], sessionToken: token ?? undefined },
        (place, status) => {
          if (
            status !== window.google.maps.places.PlacesServiceStatus.OK ||
            !place
          ) {
            onChange({ ...values, streetAddress: address });
            return;
          }
          const comps = place.address_components;
          const streetNumber = extractComponent(comps, 'street_number');
          const route = extractComponent(comps, 'route');
          const city =
            extractComponent(comps, 'locality') ||
            extractComponent(comps, 'sublocality') ||
            extractComponent(comps, 'administrative_area_level_2');
          const state = extractComponent(
            comps,
            'administrative_area_level_1',
            true,
          );
          const zip = extractComponent(comps, 'postal_code');
          const countryLong = extractComponent(comps, 'country');
          const countryShort = extractComponent(comps, 'country', true);
          const matchedCountry = allCountries.find(
            c => c.isoCode === countryShort || c.name === countryLong,
          );
          const country = matchedCountry
            ? matchedCountry.isoCode
            : countryShort;
          const streetAddress =
            [streetNumber, route].filter(Boolean).join(' ') || address;

          setZipWarning(!zip);
          onChange({
            streetAddress,
            city,
            state,
            zipCode: zip,
            country,
          });
          setStreetInput(streetAddress);
        },
      );
    },
    [places, values, onChange, sessionToken],
  );

  const handleStreetChange = useCallback(
    val => {
      setStreetInput(val);
      onChange({ ...values, streetAddress: val });
    },
    [values, onChange],
  );

  const handleFieldChange = useCallback(
    field => e => {
      if (field === 'zipCode') setZipWarning(false);
      onChange({ ...values, [field]: e.target.value });
    },
    [values, onChange],
  );

  const stateOptions = values.country
    ? State.getStatesOfCountry(values.country)
    : State.getStatesOfCountry('US');

  return (
    <>
      <HiddenMap ref={mapRef} />
      <label>
        Street Address:<RequiredMark>*</RequiredMark>
        <br />
        <PlacesAutocomplete
          value={streetInput}
          onChange={handleStreetChange}
          onSelect={handleSelect}
          debounce={300}
          searchOptions={{ types: ['address'], ...(sessionToken ? { sessionToken } : {}) }}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <AutocompleteWrapper>
              <TextField
                fullWidth
                margin="dense"
                error={showError('streetAddress')}
                helperText={errorText('streetAddress')}
                {...getInputProps({
                  placeholder: 'Street Address',
                  required: true,
                })}
              />
              <SuggestionsDropdown
                suggestions={suggestions}
                loading={loading}
                getSuggestionItemProps={getSuggestionItemProps}
              />
            </AutocompleteWrapper>
          )}
        </PlacesAutocomplete>
      </label>
      <br />
      <label>
        Country:<RequiredMark>*</RequiredMark>
        <br />
        <FormControl
          fullWidth
          variant="outlined"
          error={showError('country')}
        >
          <Select
            value={values.country || ''}
            onChange={handleFieldChange('country')}
            required
            displayEmpty
          >
            <MenuItem value="" disabled>
              <Placeholder>Country</Placeholder>
            </MenuItem>
            {COUNTRIES.map(c => (
              <MenuItem key={c.isoCode} value={c.isoCode}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
          {showError('country') && (
            <FormHelperText>{errorText('country')}</FormHelperText>
          )}
        </FormControl>
      </label>
      <br />
      <br />
      <label>
        City:<RequiredMark>*</RequiredMark>
        <br />
        <TextField
          fullWidth
          placeholder="City"
          value={values.city || ''}
          onChange={handleFieldChange('city')}
          required
          error={showError('city')}
          helperText={errorText('city')}
        />
      </label>
      <br />
      <br />
      <label>
        State / Province:<RequiredMark>*</RequiredMark>
        <br />
        {stateOptions.length > 0 ? (
          <FormControl
            fullWidth
            variant="outlined"
            error={showError('state')}
          >
            <Select
              value={values.state || ''}
              onChange={handleFieldChange('state')}
              required
              displayEmpty
            >
              <MenuItem value="" disabled>
                <Placeholder>State / Province</Placeholder>
              </MenuItem>
              {stateOptions.map(s => (
                <MenuItem key={s.isoCode} value={s.isoCode}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            {showError('state') && (
              <FormHelperText>{errorText('state')}</FormHelperText>
            )}
          </FormControl>
        ) : (
          <TextField
            fullWidth
            placeholder="State / Province"
            value={values.state || ''}
            onChange={handleFieldChange('state')}
            required
            error={showError('state')}
            helperText={errorText('state')}
          />
        )}
      </label>
      <br />
      <br />
      <label>
        Zip Code:<RequiredMark>*</RequiredMark>
        <br />
        <TextField
          fullWidth
          placeholder="Zip Code"
          value={values.zipCode || ''}
          onChange={handleFieldChange('zipCode')}
          required
          error={showError('zipCode')}
          helperText={zipWarning ? 'ZIP code not found for this address, please enter it manually' : errorText('zipCode')}
        />
      </label>
    </>
  );
}
